/**
 * Enhanced Print Export for Traveller Ship Builder
 * 
 * This utility provides a clean, optimized printing experience for the Ship Summary page.
 * It uses browser-native functionality with custom styling and DOM manipulation to create
 * professional, print-ready output.
 */

export const printShipSheet = () => {
  console.log('Starting enhanced print export...');

  // Find the print container
  const printContainer = document.getElementById('print-container');
  if (!printContainer) {
    console.error('Print container not found');
    return;
  }

  // Create a clone of the container for manipulation
  const clonedContainer = printContainer.cloneNode(true) as HTMLElement;
  
  // Remove all interactive elements
  const elementsToRemove = clonedContainer.querySelectorAll(
    '.save-load-buttons, input[type="text"], input[type="number"], button, select, textarea, [onclick], [onchange], [oninput], [contenteditable="true"]'
  );
  
  elementsToRemove.forEach(element => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });

  // Remove specific text elements that are not needed in print
  const textElementsToRemove = clonedContainer.querySelectorAll('p');
  textElementsToRemove.forEach(element => {
    if (element.textContent && element.textContent.includes('Click the × button to remove the image')) {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
  });

  // Convert input fields to static text
  const textInputs = clonedContainer.querySelectorAll('input[type="text"]');
  textInputs.forEach(input => {
    const inputElement = input as HTMLInputElement;
    const value = inputElement.value || inputElement.placeholder || '';
    const div = document.createElement('div');
    div.textContent = value;
    div.className = 'px-3 py-2 h-10 flex items-center text-base text-gray-900';
    div.style.cssText = 'padding: 0.5rem 0.75rem; height: 2.5rem; display: flex; align-items: center; font-size: 1rem; color: #111827;';
    
    if (inputElement.parentNode) {
      inputElement.parentNode.replaceChild(div, inputElement);
    }
  });

  // Convert number inputs to static text
  const numberInputs = clonedContainer.querySelectorAll('input[type="number"]');
  numberInputs.forEach(input => {
    const inputElement = input as HTMLInputElement;
    const value = inputElement.value || inputElement.placeholder || '';
    const div = document.createElement('div');
    div.textContent = value;
    div.className = 'px-3 py-2 h-10 flex items-center text-base text-gray-900';
    div.style.cssText = 'padding: 0.5rem 0.75rem; height: 2.5rem; display: flex; align-items: center; font-size: 1rem; color: #111827;';
    
    if (inputElement.parentNode) {
      inputElement.parentNode.replaceChild(div, inputElement);
    }
  });

  // Apply print-specific styles to the cloned container
  clonedContainer.style.cssText = `
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 210mm !important;
    max-width: 210mm !important;
    margin: 0 !important;
    padding: 15mm !important;
    background: white !important;
    font-size: 10pt !important;
    line-height: 1.2 !important;
    font-family: Arial, sans-serif !important;
    visibility: visible !important;
    z-index: 9999 !important;
  `;

  // Add the cloned container to the body
  document.body.appendChild(clonedContainer);

  // Hide all other elements
  const allElements = document.body.querySelectorAll('*');
  allElements.forEach(element => {
    if (element !== clonedContainer && !clonedContainer.contains(element)) {
      (element as HTMLElement).style.visibility = 'hidden';
    }
  });

  // Trigger print
  window.print();

  // Cleanup after printing
  const cleanup = () => {
    // Remove the cloned container
    if (clonedContainer.parentNode) {
      clonedContainer.parentNode.removeChild(clonedContainer);
    }

    // Restore visibility of all elements
    allElements.forEach(element => {
      if (element !== clonedContainer && !clonedContainer.contains(element)) {
        (element as HTMLElement).style.visibility = '';
      }
    });

    // Remove event listeners
    window.removeEventListener('afterprint', cleanup);
  };

  // Add cleanup event listener
  window.addEventListener('afterprint', cleanup);

  // Fallback cleanup in case afterprint doesn't fire
  setTimeout(cleanup, 1000);
}; 